<template>
    <Modal v-model="model" :title="'Krank melden'" :subtitle="calendarMonth.format('MMMM yyyy')" @view:willPresent="onViewWillPresent">

        <template #default>
            <div><!--ScrollBox-->

                <template v-if="step === 1">

                    <AInesMostRecentMessage v-if="showMostRecentAInesMessage" />

                    <h2 class="globalHeader">1. Kranktage auswählen</h2>
                    <IonLabel>
                        Markiere die Tage deiner Krankmeldung mit einem <AbsenceBubble :letter="getLabelForAbsenceType(AbsenceType.Sickness,true)"/> im Kalender.
                    </IonLabel>

    <!--                    <ion-datetime-->
    <!--                        locale="de-DE"-->
    <!--                        :first-day-of-week="1"-->
    <!--                        presentation="month-year"-->
    <!--                        @ionChange="(e) => { if (e.target.value) { updateMonthSelection(e.target.value) } } "-->
    <!--                        color="secondary"-->
    <!--                        ref="datetime"-->
    <!--                    />-->

                    <Skeleton v-if="initialDataLoader.pending.value" :items="['block--height-20em']"/>
                    <div v-else class="rich-month-calendar">
                        <MonthCalendarGrid
                            :date="calendarMonth.getId()"
                            :showDetailsOnClick="false"
                            v-on:date-click="onCalendarDateClick"
                            :is-content-same-as-date-before="(date:CalendarDate, dateBefore:CalendarDate) => isDateSameCombinedAsDateBefore(date.id, dateBefore.id)"
                        >
                            <template v-slot="{ date, adjacent, sameAsBefore }">
                                <div
                                    v-if="date && !adjacent && isCombinedAbsent(date.id)"
                                    class="calendar-date-content absence"
                                    :class="{large: isCombinedSickness(date.id), 'same-as-before': sameAsBefore}"
                                >
                                    <div class="dot">{{
                                            isCombinedSickness(date.id) ? getLabelForAbsenceType(getCombinedAbsenceType(date.id) as AbsenceType, true) : ''
                                        }}</div>
                                </div>
                                <div
                                    v-if="date && !adjacent && getBooking(date.id)"
                                    class="calendar-date-content booked"
                                ><div class="dot" /></div>
                            </template>
                        </MonthCalendarGrid>
                        <MonthCalendarLegend mode="sickness"/>
                    </div>
                </template>

                <template v-else-if="step === 2">

                    <h2 class="globalHeader">2. Zieldienste korrigieren</h2>
                    <IonLabel>
                        Hier kannst du überprüfen, ob die reduzierten Zieldienste deinen Wünschen entsprechen.
                    </IonLabel>

                    <Skeleton v-if="initialDataLoader.pending.value" :items="['block--height-5em', 'block--height-5em', 'margin']"/>
                    <template v-else>
                        <PlusMinusInput
                            v-model="targetBookingInput"
                            @update:modelValue="targetBookingInputUnchanged = false"
                            :min="targetBookingMin"
                            :max="targetBookingMax"
                            :unit="pluralizeUnitOnly(targetBookingInput, 'Dienst', 'Dienste')"/>

                        <WorktimeSummaryLine v-if="worktimeMode" :worktimeMode="worktimeMode" :worktimeTargetDiff="renderSet.spotOnDiff" :worktimeTarget="renderSet.spotOnTargetBookings" :sickDays="renderSet.countSickness">
                            <template v-slot:info>
                                <p><strong>
                                    <IonIcon :icon="informationCircle" id="overlay-trigger"
                                             class="globalTextAlignedIcon"/>
                                    Krankheitstage</strong></p>
                                <p>
                                    Krankheitstage werden anteilig zu den Vertragsstunden angerechnet. Bei {{renderSet.contractHours}} Vertragsstunden sind das im {{ calendarMonth.format('MMMM') }} voraussichtlich ca. {{floatFormat(renderSet.hourValueAbsence,1)}}h/Werktag*.</p>

                                <!-- noch weniger als 12 diesnte arbeiten? in den dienstplaneinstellung können die wunschdiesnte weiter reduziuert werden -->

                                <p>
                                    <strong>{{renderSet.countSickness}} Krankheitstage</strong> entsprechen damit {{pluralize(renderSet.spotOnDiff, 'Dienst', 'Diensten', undefined, true)}} bzw. insgesamt <strong>{{pluralize(renderSet.spotOnTargetBookings, 'Solldienst', 'Solldiensten')}}</strong> (je ca. {{floatFormat(renderSet.hourValueBooking, 2) }}h) im {{calendarMonth.format('MMMM')}}.</p>
                                <sub>
                                    <strong>*</strong> Dies ist eine Prognose. Die exakte Bewertung
                                    steht erst zur Monatsabrechnung fest.
                                </sub>
                            </template>
                        </WorktimeSummaryLine>
                    </template>

                    <template v-if="affectedBookingsByCombinedSicknesses.length > 0 && initialDataLoader.done.value">
                        <h2 class="globalHeader">3. Stationen informieren</h2>
                        <IonLabel>
                            <Ines/> sucht selbstständig nach einem Ersatz für den Ausfall und storniert automatisch, falls niemand einspringen kann. Die folgenden Dienste sind durch den Krankheitsausfall betroffen, die Stationen müssen entsprechend informiert werden.
                        </IonLabel>
                        <br><br>
                        <RichResponseListItem
                            v-for="booking in affectedBookingsByCombinedSicknesses"
                            :title="getLabelForShift(booking.shiftType) + ': ' + booking?.jobsite.abbreviation"
                            :icon="booking.date"
                            icon-type="date"
                            :class="booking"
                            :image="getImageForJobsite(booking.jobsite)"
                            @click="showDayDetailModal = booking?.date"
                        >
    <!--                        <ion-chip class="small">-->
    <!--                            {{ getLabelForShift(booking.shiftType) }}-->
    <!--                        </ion-chip>-->
    <!--                        {{ datetime.formatKapitelDateTime(booking.begin, datetime.kapitelDateTimeFormatTime) }} - {{ datetime.formatKapitelDateTime(booking.until, datetime.kapitelDateTimeFormatTime) }} Uhr-->
                            <div>
                                <Contact display-mode="standalone" v-if="booking?.shiftInformation.requestedBy" :contact="booking?.shiftInformation.requestedBy" />
                                <Contact display-mode="standalone" v-else-if="booking?.jobsite.contact" :contact="booking?.jobsite.contact" />
                                <br>
                                <p>Storno {{ withdrawalDeadlineText(booking) }}</p>
                            </div>
                        </RichResponseListItem>
                    </template>

                </template>

                <CalendarDetail v-if="model" v-model="showDayDetailModal" :date-id="showDayDetailModal"></CalendarDetail>

            </div>
        </template>

        <template #actions>
            <ion-button
                color="primary"
                expand="block"
                :disabled="!!preventSubmitMessage"
                @click="() => step === 1 ? step++ : onClickOk()"
            >
                {{ preventSubmitMessage || (step === 1 ? 'Weiter' : submitMessage) }}
            </ion-button>
            <ion-button
                color="light"
                expand="block"
                @click="() => step === 1 ? model = false : step--"
            >
                {{ step === 1 ? 'Abbrechen' : 'Zurück' }}
            </ion-button>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue'
import {alertController, IonButton, IonIcon, IonLabel, IonList} from '@ionic/vue';
import {computed, PropType, ref, Ref, watch} from 'vue';
import {generateCalendarMonth} from "@/helper/calendar/calendarMonth";
import date from "@/helper/datetime/date";

import {fetchPlanningTemplate, getWorktimeMode, updateAutopilotPreference} from "@/helper/autopilotPreference";
import {DataLoader} from "@/helper/dataLoader";

import Skeleton from "@/components/Skeleton.vue";
import {floatFormat, pluralize, pluralizeUnitOnly} from '@/helper/amountFormatter';
import {
    Absence,
    AbsenceType,
    AutopilotPreference,
    AutopilotStatus,
    Day,
    PlanningTemplate
} from "@/graphql/generated/graphql";
import {fetchAutopilotStatus, fetchSpotOnBookingTarget} from "@/helper/autopilotStatus";

import {kapitelDateString, kapitelDateTimeString} from '@/graphql/kapitelTypes';
import PlusMinusInput from "@/components/PlusMinusInput.vue";
import MonthCalendarLegend from "@/components/MonthCalendarLegend.vue";
import {DayMap, fetchDaysForMonth, generateDay, toDayMap} from "@/helper/day";
import {generateAbsence, getLabelForAbsenceType, removeAbsence, setAbsence} from "@/helper/absence";
import {useEmployeeStore} from "@/store/employee";
import MonthCalendarGrid from "@/components/MonthCalendarGrid.vue";
import Contact from "@/views/CalendarDetail/components/Contact.vue";
import RichResponseListItem from "@/views/Chat/RichResponses/components/RichResponseListItem.vue";
import {getImageForJobsite} from "@/helper/jobsite";
import {getLabelForShift} from "../../../helper/shiftTypes";
import datetime from "@/helper/datetime/datetime";
import WorktimeSummaryLine from "@/views/components/Planning/WorktimeSummaryLine.vue";
import {informationCircle} from "ionicons/icons";
import {consoleLog} from "@/helper/console";
import CalendarDetail from "@/views/CalendarDetail/CalendarDetail.vue";
import AbsenceBubble from "@/components/AbsenceBubble.vue";
import AInesMostRecentMessage from "@/views/Chat/AInesMostRecentMessage.vue";
import {CalendarDate} from '@/helper/calendar/calendarDate';
import Ines from "@/views/components/Ines.vue";
import {DateRange} from "@/helper/datetime/dateRange";
import {computeDeadlineForRequestFinish, createBookingWithdrawalRequest} from "@/helper/bookingWithdrawalRequest";
import {BookingWithdrawalRequestUnclaimedConsequence, Booking as BookingType} from "@/graphql/generated/graphql";
import {kapitelErrorHandler} from "@/helper/error";

const model = defineModel({type: Boolean})

const step : Ref<1|2> = ref(1)

const props = defineProps({
    initialMonth: {
        type: String as PropType<kapitelDateString>,
        required: false,
        default: date.startOfMonth(date.getToday())
    },
    showMostRecentAInesMessage: {
        type: Boolean,
        required: true,
    }
});

const initialDataLoader = new DataLoader();
const monthSelection = ref(props.initialMonth);
// const updateMonthSelection = (dateTime) => {
//     monthSelection.value = date.startOfMonth(datetime.convertDateTime2Date(dateTime))
// }
const calendarMonth = computed(() => generateCalendarMonth(monthSelection.value));

const showDayDetailModal : Ref<kapitelDateString | undefined> = ref(undefined)

/**
 * employee
 */
const employee = ref()
initialDataLoader.add(
    async () =>  employee.value = await useEmployeeStore().getEmployee()
)





/**
 * sickness days
 */
const isSicknessType = (type: AbsenceType) => type === AbsenceType.Sickness

const days: Ref<DayMap> = ref(new Map() as DayMap)
const getDay = (date: kapitelDateString): Day | undefined => days.value.get(date)
const getAbsence = (date: kapitelDateString) => getDay(date)?.absence
const getBooking = (date: kapitelDateString) => getDay(date)?.booking ?? undefined
const getSickness = (date: kapitelDateString) => getAbsence(date) && isSicknessType((getAbsence(date) as Absence).type) ? getAbsence(date) : undefined

const modifiedDays: Ref<DayMap> = ref(new Map() as DayMap)
const getModDay = (date: kapitelDateString) => modifiedDays.value.get(date)
const getModDays = () => Array.from(modifiedDays.value.values())
const getModAbsence = (date: kapitelDateString) => getModDay(date)?.absence || undefined
const getModAbsences = () => getModDays().filter(d => d.absence).map(d => d.absence as Absence)
const isModSickness = (date: kapitelDateString) => (getModAbsence(date) && isSicknessType((getModAbsence(date) as Absence).type))
const getModSicknesses = () => getModAbsences().filter(a => isModSickness(a.date))


const getCombinedDay = (date: kapitelDateString) => getModDay(date) || getDay(date)
const getCombinedDays = () => Array.from(days.value.values()).map(d => getCombinedDay(d.date) as Day)
const getCombinedAbsence = (date: kapitelDateString) => getCombinedDay(date)?.absence || undefined
const getCombinedAbsences = () => getCombinedDays().filter(d => d.absence).map(d => d.absence as Absence)
const isCombinedAbsent = (date: kapitelDateString) => !!getCombinedAbsence(date)
const getCombinedAbsenceType = (date: kapitelDateString) => getCombinedAbsence(date)?.type
const isCombinedSickness = (date: kapitelDateString) => isCombinedAbsent(date) && isSicknessType((getCombinedAbsence(date) as Absence).type)
const isDateSameCombinedAsDateBefore = (date: kapitelDateString, dateBefore: kapitelDateString) => {
    return (isCombinedAbsent(date) && isCombinedAbsent(dateBefore) && isCombinedSickness(date) === isCombinedSickness(dateBefore)) ||
        (getBooking(date) && getBooking(dateBefore))
}
const combinedSicknesses = computed(() => getCombinedAbsences().filter(a => isSicknessType(a.type)))
const affectedBookingsByCombinedSicknesses = computed(() => combinedSicknesses.value.map(a => a.date).map(getBooking).filter(b => !!b))

initialDataLoader.add(
    async () => {
        modifiedDays.value.clear()
        days.value = toDayMap(await fetchDaysForMonth(calendarMonth.value))
    }
)


const saveModifiedDays = async () => {
    const newSicknessDates = Array.from(modifiedDays.value.entries())
        .filter(([date, day]) => {
            const hasNewSickness = !!day.absence && isSicknessType((day.absence as Absence).type)
            const hadSicknessBefore = !!getSickness(date)
            return hasNewSickness && !hadSicknessBefore
        })
        .map(([date]) => date)

    // create withdrawal requests for bookings on newly marked sickness days first
    const bookingsToWithdraw: (BookingType | undefined)[] = newSicknessDates.map(getBooking)
    for (const booking of bookingsToWithdraw) {
        if (!booking) continue
        try {
            await createBookingWithdrawalRequest(
                booking,
                BookingWithdrawalRequestUnclaimedConsequence.Cancel,
                "Krankmeldung"
            )
        } catch (error) {
            kapitelErrorHandler("Erstellen der Ersatzsuche fehlgeschlagen")
            // abort saving absences if any request fails
            return
        }
    }

    modifiedDays.value.forEach((day, date) => {
        if (!day.absence && getDay(date)?.absence) {
            removeAbsence(getDay(date)?.absence as Absence)
        } else if (day.absence) {
            setAbsence(day.absence)
        }
    })

    modifiedDays.value.clear()
}

const setModAbsence = (date: kapitelDateString, type: AbsenceType) => {
    const day = generateDay(date)
    day.absence = generateAbsence(date, type)
    modifiedDays.value.set(date, day)
}
const removeModAbsence = (date: kapitelDateString) => {
    if (getSickness(date)) {
        const day = generateDay(date)
        modifiedDays.value.set(date, day)
    } else {
        modifiedDays.value.delete(date)
    }
}

const onCalendarDateClick = async ({date}: { date: kapitelDateString }) => {
    if (isCombinedSickness(date)) {
        if (!isModSickness(date)) {
            const alert = await alertController.create({
                header: 'Krankmeldung',
                message: 'Dieser Tag ist bereits Teil einer älteren Krankmeldung.',
                buttons: ['Ok']
            })
            alert.present()
            return
        }

        removeModAbsence(date)

        const reFilled = _fillConsecutive(AbsenceType.Sickness)
        if (reFilled.length === 1 && reFilled[0] == date) {
            const alert = await alertController.create({
                header: 'Krankmeldung',
                message: 'Die Tage einer Krankmeldung müssen zusammenhängend sein.',
                buttons: ['Ok']
            })
            alert.present()
        }
    } else {
        setModAbsence(date, AbsenceType.Sickness)
        _fillConsecutive(AbsenceType.Sickness)
    }

}

const _fillConsecutive = (type: AbsenceType) : kapitelDateString[] => {
    const result : kapitelDateString[] = []
    const days = Array.from(modifiedDays.value.keys())
    if (days.length < 2) {
        return result
    }
    const min = date.min(...days)
    const max = date.max(...days)
    const range = new DateRange(min, max)
    range.getAllDates().forEach((d) => {
        if (!modifiedDays.value.has(d)) {
            setModAbsence(d, AbsenceType.Sickness)
            result.push(d)
        }
    })
    return result
}



/**
 * worktime target
 */

// +/- input value
const targetBookingInput = ref(0)
const targetBookingInputUnchanged = ref(true)

const planningTemplate: Ref<undefined | PlanningTemplate> = ref(undefined)
const autopilotStatus: Ref<AutopilotStatus | null | undefined> = ref()
const autopilotPreference: Ref<AutopilotPreference | undefined> = ref();

// initial diff between bookingTarget & spotOn value
const initialWorktimeTargetDiff: Ref<number> = ref(0);

// varies by number of sickness days, gets updated as sickess days are added
const currentSpotOnBookingTarget = ref(0);
const currentSpotOnBookingTargetLoading = ref(false);

initialDataLoader.add(
    async () => {
        planningTemplate.value = await fetchPlanningTemplate(calendarMonth.value)

        autopilotStatus.value = await fetchAutopilotStatus(calendarMonth.value)
        if (!autopilotStatus.value) {
            throw new Error("Dienstplanung für " + calendarMonth.value.format() + " noch nicht gestartet")
        }
        autopilotPreference.value = autopilotStatus.value.autopilotPreferences

        if (autopilotPreference.value?.bookingTarget) {
            // already saved bookingTarget
            targetBookingInput.value = autopilotPreference.value?.bookingTarget
        } else if (autopilotStatus.value.bookingTarget) {
            // take infered from worktimemode setting
            targetBookingInput.value = autopilotStatus.value.bookingTarget
        }
        targetBookingInputUnchanged.value = true

        // init by existing spot on target
        currentSpotOnBookingTarget.value = planningTemplate.value.bookingTargetSpotOn

        // initial diff
        initialWorktimeTargetDiff.value = targetBookingInput.value - currentSpotOnBookingTarget.value

        consoleLog('SICK', 'initialized sickness wizard with bookingTarget:%i, spotOnTarget:%i (offset:%i)', targetBookingInput.value, currentSpotOnBookingTarget.value, initialWorktimeTargetDiff.value)
    }
);

// spot on + offset as min bc. currently we don't have plannedAbsencePlanning as part of sickness dialog
const targetBookingMin = computed(() => {
    const options = []
    if (planningTemplate.value !== undefined) {
        options.push(planningTemplate.value.bookingTargetMin)
    }
    options.push(currentSpotOnBookingTarget.value + initialWorktimeTargetDiff.value)
    return Math.max(...options)
})
const targetBookingMax = computed(() => planningTemplate.value?.bookingTargetMax ? planningTemplate.value.bookingTargetMax : undefined)



/**
 * RenderSets
 */

const worktimeMode = computed(() => currentSpotOnBookingTarget.value !== undefined ? getWorktimeMode(targetBookingInput.value, currentSpotOnBookingTarget.value) : undefined )

const renderSet = computed(() => {
    const t = planningTemplate.value

    const hourValueBooking = t?.averageShiftDuration || 0
    const hourValueAbsence = t?.absenceDayHoursFloat || 0

    const missingBookings = t ? t.bookingTargetSpotOn - targetBookingInput.value : 0
    const missingHours = missingBookings * hourValueBooking

    const contractHours = autopilotStatus.value?.contractHours

    const countSickness = combinedSicknesses.value?.length
    const spotOnTargetBookings = currentSpotOnBookingTarget.value ?? 0

    const spotOnDiff = (spotOnTargetBookings && targetBookingInput.value) ? targetBookingInput.value - spotOnTargetBookings : 0

    return {
        hourValueBooking,
        hourValueAbsence,
        missingBookings,
        missingHours,
        contractHours,
        countSickness,
        spotOnTargetBookings,
        spotOnDiff,
    }
})





/**
 * overall
 */

const saveAutopilotPreference = async () => {
    if (targetBookingInput.value !== autopilotPreference.value?.bookingTarget) {
        consoleLog('SICK', 'saving bookingTarget:%i', targetBookingInput.value)
        await updateAutopilotPreference(calendarMonth.value, {bookingTarget: targetBookingInput.value});
    }
}

const preventSubmitMessage = computed(() => {
    if (currentSpotOnBookingTargetLoading.value) return 'Berechnung läd...'
    if (modifiedDays.value.size === 0) return "Kranktage auswählen"
    return undefined
})
const submitMessage = computed(() => pluralize(getModSicknesses().length, 'Tag', 'Tage') + ' krank melden')

const withdrawalDeadlineText = (booking: BookingType) => {
    const dl = computeDeadlineForRequestFinish(booking, BookingWithdrawalRequestUnclaimedConsequence.Cancel)
    return datetime.formatKapitelDateTime(dl, 'dd.LL. HH:mm')
}

const onClickOk = async () => {
    await saveAutopilotPreference()
    await saveModifiedDays()
    model.value = false
}

// update spot on booking target as sicknesses get added or removed
watch (() => modifiedDays.value.values(), async () => {
    currentSpotOnBookingTargetLoading.value = true
    currentSpotOnBookingTarget.value = await fetchSpotOnBookingTarget(
        calendarMonth.value,
        combinedSicknesses.value.map(sickness => ({date: sickness.date, type: sickness.type}))
    )
    consoleLog('SICK', 'refetched spotOnTarget:%i', currentSpotOnBookingTarget.value)
    currentSpotOnBookingTargetLoading.value = false

    // reapply original diff to spotOnTarget
    if (targetBookingInputUnchanged.value) {
        targetBookingInput.value = Math.max(currentSpotOnBookingTarget.value + initialWorktimeTargetDiff.value, 0)
        consoleLog('SICK', 'reapplied initial offset (%i) as bookingTarget:%i', currentSpotOnBookingTarget.value, targetBookingInput.value)
    }
})


/**
 * reset
 */

const reset = async () => {
    await initialDataLoader.reload()

}
watch(()=>calendarMonth.value, () => reset())

// init
const onViewWillPresent = () => reset()

</script>

<style lang="scss" scoped>

// FOLDING BOX
.foldableContainer {
    overflow: hidden;
}

.foldable {
    margin-top: -500px;
    transition: all 1s;
}

.foldable.expanded {
    margin-top: 0;

}

// CALENDAR
.rich-month-calendar {
    background-color: var(--ion-color-light);
    border-radius: 1rem;
    padding: 0.5em 1em 1em 1em;
    width: 100%;
    margin-top: 0.5em;
}
:deep(.rich-month-calendar) {
    .cell {
        .calendar-date-content {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;

            > .dot {
                font-size: var(--custom-font-size-regular);
                $SIZE: 0.5em;
                width: $SIZE;
                height: $SIZE;
                overflow: hidden;
                text-align: center;
                border-radius: 50%;
                transform: translateY(1.1em);
                position: relative;
                font-weight: var(--custom-font-weight-bold);
                background-color: currentColor;
            }

            &.absence {
                color: var(--ion-color-red);
            }

            &.large {
                > .dot {
                    $SIZE: 0.9rem;
                    width: $SIZE;
                    height: $SIZE;
                    font-size: $SIZE * 0.9;
                    line-height: $SIZE;
                    transform: translateY(calc($SIZE + 0.3em));
                    background-color: var(--ion-color-red);;
                    color: var(--ion-color-light);
                }
            }

            &.booked {
                color: var(--ion-color-black-tint);
            }
            &.available {
                color: var(--ion-color-green);
            }

            &.same-as-before {
                &:before {
                    content: '····································';
                    position: absolute;
                    letter-spacing: 0.1em;
                    top: 50%;
                    left: -50%;
                    width: 100%;
                    overflow: hidden;
                    text-wrap: nowrap;
                    transform: translateY(0.4em);
                    color: currentColor;

                    pointer-events: none;
                }
            }
        }
    }
}

</style>
